import React from "react";
import { interpolate, useCurrentFrame } from "remotion";
import { loadFont } from "@remotion/google-fonts/FiraCode";

const { fontFamily: firaCode } = loadFont();

type CodeTypewriterProps = {
  code: string;
  charFrames?: number;
  delay?: number;
  fontSize?: number;
  tokenColors?: Partial<Record<TokenType, string>>;
};

type TokenType = "keyword" | "string" | "comment" | "decorator" | "function" | "number" | "normal";

const DARK_TOKEN_COLORS: Record<TokenType, string> = {
  keyword: "#89B4FA",
  string: "#A6E3A1",
  comment: "#6C7086",
  decorator: "#CBA6F7",
  function: "#F5C2E7",
  number: "#F9E2AF",
  normal: "#CDD6F4",
};

type Token = { text: string; type: TokenType };

const KEYWORDS = new Set([
  "from", "import", "def", "async", "return", "if", "else", "class", "True", "False", "None",
  "export", "const", "function", "let", "var", "await", "new", "throw", "try", "catch",
  "null", "true", "false", "method", "status", "url",
]);

const tokenizeLine = (line: string): Token[] => {
  const tokens: Token[] = [];
  let i = 0;

  // Comment (// or #)
  const trimmed = line.trimStart();
  if (trimmed.startsWith("#") || trimmed.startsWith("//")) {
    return [{ text: line, type: "comment" }];
  }

  // Decorator
  if (trimmed.startsWith("@")) {
    return [{ text: line, type: "decorator" }];
  }

  while (i < line.length) {
    // Whitespace
    if (line[i] === " " || line[i] === "\t") {
      let end = i;
      while (end < line.length && (line[end] === " " || line[end] === "\t")) end++;
      tokens.push({ text: line.slice(i, end), type: "normal" });
      i = end;
      continue;
    }

    // Strings (single or double quotes)
    if (line[i] === '"' || line[i] === "'") {
      const quote = line[i];
      let end = i + 1;
      while (end < line.length && line[end] !== quote) end++;
      end = Math.min(end + 1, line.length);
      tokens.push({ text: line.slice(i, end), type: "string" });
      i = end;
      continue;
    }

    // Numbers
    if (/[0-9]/.test(line[i])) {
      let end = i;
      while (end < line.length && /[0-9.]/.test(line[end])) end++;
      tokens.push({ text: line.slice(i, end), type: "number" });
      i = end;
      continue;
    }

    // Words (keywords, functions, identifiers)
    if (/[a-zA-Z_]/.test(line[i])) {
      let end = i;
      while (end < line.length && /[a-zA-Z_0-9]/.test(line[end])) end++;

      const word = line.slice(i, end);
      if (KEYWORDS.has(word)) {
        tokens.push({ text: word, type: "keyword" });
      } else if (end < line.length && line[end] === "(") {
        tokens.push({ text: word, type: "function" });
      } else {
        tokens.push({ text: word, type: "normal" });
      }
      i = end;
      continue;
    }

    // Other characters (operators, parens, braces, etc.)
    tokens.push({ text: line[i], type: "normal" });
    i++;
  }

  return tokens;
};

const CURSOR_BLINK_FRAMES = 16;

export const CodeTypewriter: React.FC<CodeTypewriterProps> = ({
  code,
  charFrames = 2,
  delay = 0,
  fontSize = 15,
  tokenColors,
}) => {
  const frame = useCurrentFrame();
  const localFrame = Math.max(0, frame - delay);

  const colors = tokenColors
    ? { ...DARK_TOKEN_COLORS, ...tokenColors }
    : DARK_TOKEN_COLORS;

  const totalChars = code.length;
  const typedCount = Math.min(totalChars, Math.floor(localFrame / charFrames));

  const typedCode = code.slice(0, typedCount);
  const lines = typedCode.split("\n");

  // Cursor blink
  const cursorOpacity = interpolate(
    frame % CURSOR_BLINK_FRAMES,
    [0, CURSOR_BLINK_FRAMES / 2, CURSOR_BLINK_FRAMES],
    [1, 0, 1],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" },
  );

  return (
    <div
      style={{
        fontFamily: firaCode,
        fontSize,
        lineHeight: 1.7,
        whiteSpace: "pre",
        color: colors.normal,
      }}
    >
      {lines.map((line, lineIdx) => {
        const tokens = tokenizeLine(line);
        const isLastLine = lineIdx === lines.length - 1;

        return (
          <div key={lineIdx} style={{ minHeight: fontSize * 1.7 }}>
            {tokens.map((token, tIdx) => (
              <span key={tIdx} style={{ color: colors[token.type] }}>
                {token.text}
              </span>
            ))}
            {isLastLine && typedCount < totalChars && (
              <span
                style={{
                  opacity: cursorOpacity,
                  color: colors.keyword,
                  fontWeight: 700,
                }}
              >
                |
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};
